'use client';

// ЕДИНЫЙ ХУК ДАННЫХ КАБИНЕТА: СОХРАНЁННОЕ — СРАЗУ, СЕРВЕР — ФОНОМ (ТЗ ~/projects/HELP-WARM-FIX-TZ.md §1, владелец 26.10.2026:
// «дёргались элементы страницы, её каркас — кнопки сверху; 60 секунд — неверное решение»; канон — CLAUDE.md «Экран
// показывается ОДИН РАЗ», docs/HOW-IT-WORKS.md §«Данные на фронте и в приложении»).
// ОБЩИЙ in-memory стор по key + копия на устройстве (localStorage `scriptera:cache:*`, время записи — `scriptera:cache-at`,
// версия сайта — `scriptera:cache-ver`). Все компоненты с одним key видят одни данные и делят один сетевой запрос (дедуп).
//   • СОХРАНЁННОЕ (память или устройство) идёт в первый кадр ВСЕГДА, любого возраста (практика stale-while-revalidate:
//     TanStack Query, SWR, Telegram). Заглушка формы (`<Shape/>`, поле `shape`) — только когда сохранённого нет вовсе;
//   • сервер проверяется ФОНОМ при открытии экрана, возврате во вкладку/окно/панель (focus/visibility/pageshow/
//     PaneActive); не чаще раза в MOUNT_FRESH_MS на ключ (данные моложе — открытие в сеть не ходит вовсе);
//   • пришло то же — ни одной перерисовки (снимок и ссылки те же); пришло другое — меняется ТОЛЬКО изменившееся: новые
//     данные собираются из старых объектов (structural sharing, `shareStructure`), неизменённые строки — те же ссылки;
//     прокрутка держится за видимую строку (`data-warm-id`), если список прокручен;
//   • `opts.version` (справка, оглавление): данные этой версии сайта верны, пока версия та же — сервер не проверяется;
//   • ИСКЛЮЧЕНИЕ — экраны денег и статусов (`SERVER_TRUTH` плана прогрева, решение ведущего 26.10.2026): баланс токенов,
//     тариф/подписка, оплата — как прежде: сохранённое старше MOUNT_FRESH_MS в первый кадр не идёт, заглушка до ответа;
//     тумблер «бот включён» ждёт `confirmed` (ответ сервера в этой сессии);
//   • ДЁРГАНИЕ ловит КАРКАС (шапка, кнопки шапки, полоса папок, высоты), а не «данные заменились» — `lib/frame-watch.ts`;
//   • УСЛОВНЫЙ ЗАПРОС (ТЗ ~/projects/SPEED-TZ.md §10.1, П2): ответ ручки с `ETag` запоминается рядом с копией
//     (`scriptera:cache-etag`); следующий запрос того же адреса несёт `If-None-Match`, и `304` = «то же самое»: копия не
//     трогается, перерисовки нет, только время копии освежается. Копия, собранная не из одного ответа (mutate, быстрый
//     ответ, `merge`), отпечатка не имеет — запрос уходит без условия;
//   • сетевая ошибка / не-2xx / невалидное тело → error=true, данные не трогаем (кэш остаётся);
//   • 401 → сессия умерла → чистим кэш и уводим на /login (кэш чужого юзера показывать нельзя).
// Что и когда греть заранее — только файл-план `lib/app-pages/warm-plan.ts` (исполнитель — `lib/warm.ts`).
// Кэшируем ТОЛЬКО валидный ответ целиком (accept). «Нет данных» = ещё ни разу не грузили,
// а НЕ пустой результат (пустой результат = валидный объект вида {ok:true, items:[]}).
import { useCallback, useContext, useEffect, useRef, useSyncExternalStore } from 'react';
import { log as logJournal, noteTask } from './client-journal';
import { PaneActiveContext } from './pane-active';
import { readShape, type Shape } from './shape';
import { isServerTruthKey, WARM_QUEUE } from './app-pages/warm-plan';
import { beginForeground, endForeground } from './warm';

const PREFIX = 'scriptera:cache:';

/** Не чаще раза в столько мс освежаем ключ по возврату фокуса в вкладку. */
const FOCUS_MIN_MS = 15_000;

/**
 * «НЕ ПРОВЕРЯТЬ СЕРВЕР ЧАЩЕ» — 60 С (`WARM_QUEUE.recheckMs` плана прогрева). Данные ключа моложе этого — открытие экрана
 * в сеть НЕ ходит (ни свой маунт, ни повторный, ни открытие после прогрева); старше — сохранённое всё равно в первом
 * кадре, а сервер проверяется фоном. Показ это число НЕ решает (кроме экранов денег и статусов — SERVER_TRUTH).
 * Тот же порог держит очередь прогрева (warm.ts: ключ, прогретый моложе, повторно не греется).
 */
// INVARIANT(scriptera): MOUNT_FRESH_MS — только «не проверять сервер чаще»; сохранённое идёт в первый кадр всегда (кроме
// SERVER_TRUTH). Причина: владелец 26.10.2026 — дёргался каркас (кнопки сверху), а «60 секунд — неверное решение»: запрет
// показа старой копии давал заглушку на каждом экране и ломал прогрев. Канон: CLAUDE.md «Экран показывается ОДИН РАЗ»,
// docs/HOW-IT-WORKS.md §«Данные на фронте и в приложении», ТЗ ~/projects/HELP-WARM-FIX-TZ.md §1.
export const MOUNT_FRESH_MS = WARM_QUEUE.recheckMs;

/** Окно детектора каркаса (lib/frame-watch.ts): смена шапки/кнопок/высот без действия человека в первые столько мс
 *  после показа — «ДЁРГАНИЕ». */
export const FLICKER_WINDOW_MS = 2_000;

/** Время записи копий на устройстве: `{ key: ms }` одним ключом (значения `scriptera:cache:*` — сами данные,
 *  их формат читают и другие места, напр. use-favorites, — поэтому время лежит отдельно). */
const AT_KEY = 'scriptera:cache-at';

/** Ручка админки: её 401 не уводит на вход (см. revalidate, ADM-NATIVE §4.2). */
function isAdminApi(url: string): boolean {
  return url.startsWith('/api/admin/');
}

// INVARIANT(scriptera): ключи с opts.sensitive НИКОГДА не пишутся в localStorage —
// только in-memory стор. Причина: секреты (MCP-токен и т.п.) в plain localStorage
// переживают сессию и утекают при XSS/общем компе. Канон: docs/archive/AUDIT-FIX-PLAN.md#p2
const sensitiveKeys = new Set<string>();

function registerSensitive(key: string): void {
  if (sensitiveKeys.has(key) || typeof window === 'undefined') return;
  sensitiveKeys.add(key);
  try {
    window.localStorage.removeItem(PREFIX + key); // зачистка кэша, записанного до пометки sensitive
  } catch {
    /* нет доступа — не критично */
  }
}

function readAtMap(): Record<string, number> {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(AT_KEY);
    const got = raw ? (JSON.parse(raw) as unknown) : null;
    return got && typeof got === 'object' ? (got as Record<string, number>) : {};
  } catch {
    return {};
  }
}

/** Когда копия ключа легла на устройство (0 — неизвестно: записана до ред. 9 → считается старой). */
function readCacheAt(key: string): number {
  const at = readAtMap()[key];
  return typeof at === 'number' && Number.isFinite(at) ? at : 0;
}

function writeCacheAt(key: string, at: number | null): void {
  if (typeof window === 'undefined') return;
  try {
    const map = readAtMap();
    const next = { ...map };
    if (at == null) delete next[key];
    else next[key] = at;
    // Ключей, которых на устройстве больше нет, в карте не держим.
    for (const k of Object.keys(next)) if (k !== key && window.localStorage.getItem(PREFIX + k) == null) delete next[k];
    window.localStorage.setItem(AT_KEY, JSON.stringify(next));
  } catch {
    /* квота / нет доступа — копия будет считаться старой, это безопасно */
  }
}

/** Версия сайта, с которой легла копия ключа (`opts.version`): `{ key: версия }` одним ключом, как время. */
const VER_KEY = 'scriptera:cache-ver';
function readVerMap(): Record<string, string> {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(VER_KEY);
    const got = raw ? (JSON.parse(raw) as unknown) : null;
    return got && typeof got === 'object' ? (got as Record<string, string>) : {};
  } catch {
    return {};
  }
}
function writeCacheVer(key: string, ver: string | undefined): void {
  if (typeof window === 'undefined') return;
  try {
    const next = { ...readVerMap() };
    if (!ver) delete next[key];
    else next[key] = ver;
    window.localStorage.setItem(VER_KEY, JSON.stringify(next));
  } catch {
    /* квота / нет доступа — копия просто будет проверена сервером */
  }
}

/** Отпечаток ответа, из которого собрана копия ключа (`ETag` ручки, ТЗ SPEED §10.1): `{ key: { u: адрес, e: ETag } }`.
 *  Годен только для того же адреса — у ключа с другим адресом (другой отбор) отпечаток чужой. */
const ETAG_KEY = 'scriptera:cache-etag';
interface EtagMark {
  u: string;
  e: string;
}
function readEtagMap(): Record<string, EtagMark> {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(ETAG_KEY);
    const got = raw ? (JSON.parse(raw) as unknown) : null;
    return got && typeof got === 'object' ? (got as Record<string, EtagMark>) : {};
  } catch {
    return {};
  }
}
function writeCacheEtag(key: string, mark: EtagMark | undefined): void {
  if (typeof window === 'undefined' || sensitiveKeys.has(key)) return;
  try {
    const map = readEtagMap();
    const cur = map[key];
    if (cur?.u === mark?.u && cur?.e === mark?.e) return;
    const next = { ...map };
    if (!mark) delete next[key];
    else next[key] = mark;
    // Ключей, которых на устройстве больше нет, в карте не держим.
    for (const k of Object.keys(next)) if (k !== key && window.localStorage.getItem(PREFIX + k) == null) delete next[k];
    window.localStorage.setItem(ETAG_KEY, JSON.stringify(next));
  } catch {
    /* квота / нет доступа — запрос просто уйдёт без условия */
  }
}

function readCache<T>(key: string): T | null {
  if (typeof window === 'undefined' || sensitiveKeys.has(key)) return null;
  try {
    const raw = window.localStorage.getItem(PREFIX + key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

/**
 * СПИСКОВ ПЕРЕПИСОК В КЭШЕ — НЕ БОЛЬШЕ ГОРСТИ (Ф13.5, находка ревью 18.08).
 * У раздела «Сообщения» свой ключ на КАЖДЫЙ отбор (канал · аккаунт · состояние ·
 * автоматизации), да ещё и тихий прогрев соседних отборов, — ключи копятся, каждый со
 * страницей переписок. Место на устройстве не резиновое: переполнилась квота — молча
 * перестаёт писаться ВЕСЬ кэш кабинета, и скелетоны возвращаются везде. Поэтому свежие
 * списки вытесняют старые; всё остальное в кэше живёт как жило.
 */
const CAPPED_PREFIX = 'cabinet:messages';
const CAPPED_KEEP = 6;
/** Порядок записи в этой сессии: старые впереди. Ключи прошлых сессий здесь не значатся —
 *  возраст их неизвестен, и вытесняются они первыми. */
let capOrder: string[] = [];

/**
 * Кого выбросить: сначала ключи неизвестного возраста (прошлые сессии), затем самые
 * давние из записанных сейчас, — пока не останется `keep`. Чистая, чтобы её проверяли
 * юниты: ошибка тут стирает человеку кэш.
 */
export function cacheEvictions(stored: string[], written: string[], keep: number): string[] {
  const fresh = written.filter((k) => stored.includes(k));
  const unknown = stored.filter((k) => !written.includes(k));
  const ordered = [...unknown, ...fresh];
  return ordered.slice(0, Math.max(0, ordered.length - Math.max(keep, 1)));
}

/** Ключи списков переписок, лежащие на устройстве прямо сейчас. */
function cappedKeys(): string[] {
  try {
    return Object.keys(window.localStorage)
      .filter((k) => k.startsWith(PREFIX + CAPPED_PREFIX))
      .map((k) => k.slice(PREFIX.length));
  } catch {
    return [];
  }
}

function dropKeys(keys: string[]): void {
  for (const k of keys) {
    try {
      window.localStorage.removeItem(PREFIX + k);
    } catch {
      /* нет доступа — не критично */
    }
  }
  if (keys.length > 0) capOrder = capOrder.filter((k) => !keys.includes(k));
}

function capCache(key: string): void {
  if (!key.startsWith(CAPPED_PREFIX)) return;
  capOrder = [...capOrder.filter((k) => k !== key), key];
  dropKeys(cacheEvictions(cappedKeys(), capOrder, CAPPED_KEEP));
}

function writeCache<T>(key: string, value: T): void {
  if (sensitiveKeys.has(key)) return;
  try {
    window.localStorage.setItem(PREFIX + key, JSON.stringify(value));
    writeCacheAt(key, Date.now());
  } catch {
    // Квота кончилась: освобождаем место списками переписок (их легко нажить заново) и
    // пробуем ещё раз — иначе кэш кабинета молча выключился бы весь.
    const others = cappedKeys().filter((k) => k !== key);
    if (others.length === 0) return; // освобождать нечем — кэш опционален, молчим
    dropKeys(others);
    try {
      window.localStorage.setItem(PREFIX + key, JSON.stringify(value));
      writeCacheAt(key, Date.now());
    } catch {
      /* всё равно не влезает / приватный режим — тихо игнорируем, кэш опционален */
    }
  }
  capCache(key);
}

interface Snapshot {
  data: unknown;
  loading: boolean;
  stale: boolean;
  error: boolean;
  /** HTTP-статус упавшей ревалидации (404 — «нет такого»); null — сеть/невалидное тело. */
  errorStatus: number | null;
  /** Данные подтверждены сервером (ответ в этой сессии или копия моложе MOUNT_FRESH_MS). */
  confirmed: boolean;
}

interface Entry {
  /** Иммутабельный снапшот — заменяется целиком (нужно для useSyncExternalStore). */
  snap: Snapshot;
  // INVARIANT(scriptera): epoch инкрементится при mutate/reload; in-flight ответ с чужим
  // epoch выбрасывается. Причина: без этого устаревший ответ перезатирает оптимистичное
  // обновление. Канон: docs/archive/AUDIT-FIX-PLAN.md#p1 (P1-19)
  epoch: number;
  inFlight: boolean;
  /** Когда последний раз уходил живой запрос — тормоз ревалидаций по фокусу. */
  fetchedAt: number;
  listeners: Set<() => void>;
  /** Параметры последнего запроса — для перечитывания снаружи (revalidatePersistentPrefix). */
  params?: FetchParams;
  /** Когда последний раз лёг УСПЕШНЫЙ ответ экрана (0 — не было или ответ прогрева). Свежесть меряет `dataAt`. */
  settledAt: number;
  /** Быстрый ответ (fastUrl) в этой сессии уже заказан — второй раз не нужен. */
  fastTried: boolean;
  /** Когда получены показываемые данные (ответ сервера, прогрев, mutate; для копии с устройства — время её записи).
   *  Возраст ≤ MOUNT_FRESH_MS — открытие экрана сервер не проверяет. */
  dataAt: number;
  /** Версия сайта, с которой получены данные (`opts.version`): совпала с текущей — данные верны без проверки. */
  dataVer?: string;
  /** Адрес, которым получены данные, если их принёс ПРОГРЕВ: экран с другим адресом (лёгкая лента `?limit=20`,
   *  PREFETCH-CAREFUL §2 п.5) на урезанном ответе не остаётся — догружает свой. undefined — данные взяты экраном. */
  dataWarmUrl?: string;
  /** Только экраны денег и статусов (SERVER_TRUTH): старая копия в первый кадр не идёт, встаёт, если запрос упал. */
  stash: unknown;
  /** Адрес запроса в полёте и ушёл ли он прогревом — экран, открытый поверх ЛЁГКОГО прогрева (другой адрес,
   *  напр. лента `?limit=20`), не должен остаться на его урезанном ответе (PREFETCH-CAREFUL §2 п.5). */
  inFlightUrl?: string;
  inFlightWarm?: boolean;
  /** Отпечаток ответа, из которого собраны показанные данные (`ETag`, адрес) — для `If-None-Match` (ТЗ SPEED §10.1). */
  etag?: EtagMark;
}

const store = new Map<string, Entry>();

const SERVER_SNAPSHOT: Snapshot = { data: null, loading: true, stale: false, error: false, errorStatus: null, confirmed: false };

function getEntry(key: string): Entry {
  const existing = store.get(key);
  if (existing) return existing;
  const cached = readCache<unknown>(key);
  const at = cached === null ? 0 : readCacheAt(key);
  // Копия с устройства — в первый кадр ВСЕГДА (ТЗ HELP-WARM-FIX §1). Исключение — экраны денег и статусов: им только
  // свежая (≤ MOUNT_FRESH_MS), иначе — страховка на случай ошибки запроса.
  const show = cached !== null && (!isServerTruthKey(key) || isFreshAt(at));
  const entry: Entry = {
    snap: {
      data: show ? cached : null,
      loading: !show,
      stale: false,
      error: false,
      errorStatus: null,
      confirmed: show && isFreshAt(at),
    },
    epoch: 0,
    inFlight: false,
    fetchedAt: 0,
    listeners: new Set(),
    settledAt: 0,
    fastTried: false,
    dataAt: show ? at : 0,
    ...(show ? { dataVer: readVerMap()[key] } : {}),
    ...(cached !== null && readEtagMap()[key] ? { etag: readEtagMap()[key] } : {}),
    stash: show ? null : cached,
  };
  store.set(key, entry);
  return entry;
}

/** Показанные данные больше не равны одному ответу сервера (mutate, быстрый ответ, merge, забыли) — отпечаток долой. */
function dropEtag(key: string, entry: Entry): void {
  if (!entry.etag) return;
  delete entry.etag;
  writeCacheEtag(key, undefined);
}

function isFreshAt(at: number): boolean {
  return at > 0 && Date.now() - at <= MOUNT_FRESH_MS;
}

/**
 * Данные ключа ВЕРНЫ для экрана с адресом `url` — открытие сервер не проверяет: есть, без ошибки, не урезанный
 * прогрев чужим адресом, и либо моложе MOUNT_FRESH_MS, либо той же версии сайта (`version`).
 */
function isFreshFor(entry: Entry, url: string, version?: string): boolean {
  if (entry.snap.data == null || entry.snap.error) return false;
  if (entry.dataWarmUrl !== undefined && entry.dataWarmUrl !== url) return false;
  if (version && entry.dataVer === version) return true;
  return isFreshAt(entry.dataAt);
}

/**
 * ЭКРАНЫ ДЕНЕГ И СТАТУСОВ — ПЕРВЫЙ КАДР ТОЛЬКО СВЕЖИЙ (SERVER_TRUTH плана, решение ведущего 26.10.2026). Ключ из этого
 * списка, который сейчас никто не показывает (нет подписчиков), а данные в памяти старше MOUNT_FRESH_MS, — на маунте
 * нового экрана уходит в заглушку до ответа сервера. Прочие ключи сюда не попадают: их сохранённое — в кадр всегда.
 * Идемпотентна: зовётся в рендере до чтения снимка.
 */
function gateServerTruth(key: string): void {
  if (!isServerTruthKey(key)) return;
  const entry = getEntry(key);
  if (entry.listeners.size > 0 || entry.snap.data == null || isFreshAt(entry.dataAt)) return;
  entry.stash = entry.snap.data;
  entry.dataAt = 0;
  entry.snap = { data: null, loading: true, stale: false, error: false, errorStatus: null, confirmed: false };
}

/**
 * STRUCTURAL SHARING (TanStack Query `replaceEqualDeep`): новые данные собираются из старых объектов — равное по
 * значению остаётся ТОЙ ЖЕ ссылкой (строка списка, шапка, папки), меняется только изменившееся. Всё равно — вернётся
 * сам `prev` (перерисовки нет). Только простые объекты и массивы (ответы JSON).
 */
export function shareStructure<T>(prev: unknown, next: T): T {
  if (prev === next) return prev as T;
  const pa = Array.isArray(prev);
  const na = Array.isArray(next);
  if (pa && na) {
    const p = prev as unknown[];
    const n = next as unknown[];
    let same = p.length === n.length;
    const out = n.map((v, i) => {
      const s = i < p.length ? shareStructure(p[i], v) : v;
      if (s !== p[i]) same = false;
      return s;
    });
    return (same ? p : out) as T;
  }
  const plain = (v: unknown): v is Record<string, unknown> =>
    !!v && typeof v === 'object' && !Array.isArray(v) && Object.getPrototypeOf(v) === Object.prototype;
  if (!plain(prev) || !plain(next)) return next;
  const pk = Object.keys(prev);
  const nk = Object.keys(next);
  let same = pk.length === nk.length;
  const out: Record<string, unknown> = {};
  for (const k of nk) {
    const s = shareStructure(prev[k], next[k]);
    out[k] = s;
    if (s !== prev[k] || !Object.prototype.hasOwnProperty.call(prev, k)) same = false;
  }
  return (same ? prev : out) as T;
}

/** Ближайший предок, который сам прокручивается (колонка на широком экране); нет — документ (null). */
function scrollParent(el: HTMLElement): HTMLElement | null {
  for (let p = el.parentElement; p && p !== document.body && p !== document.documentElement; p = p.parentElement) {
    const oy = getComputedStyle(p).overflowY;
    if ((oy === 'auto' || oy === 'scroll' || oy === 'overlay') && p.scrollHeight > p.clientHeight + 1) return p;
  }
  return null;
}

/**
 * ПРОКРУТКА ДЕРЖИТСЯ ЗА ВИДИМУЮ СТРОКУ (ТЗ HELP-WARM-FIX §1, приём браузеров «scroll anchoring», которого нет в Safari):
 * до подмены данных запоминаем первую видимую строку списка (`[data-warm-id]`) и её верх; после коммита React (кадр
 * до отрисовки) возвращаем её на то же место. Список у самого верха — не держим: новые строки сверху видны сразу.
 */
function captureScrollAnchor(): (() => void) | null {
  if (typeof document === 'undefined' || typeof requestAnimationFrame === 'undefined') return null;
  const vh = window.innerHeight;
  for (const el of document.querySelectorAll<HTMLElement>('[data-warm-id]')) {
    const r = el.getBoundingClientRect();
    if (r.height === 0 || r.bottom <= 0 || r.top >= vh) continue;
    const root = scrollParent(el);
    if ((root ? root.scrollTop : window.scrollY) <= 0) return null;
    const id = el.dataset.warmId ?? '';
    const top = r.top;
    return () => {
      const now = [...document.querySelectorAll<HTMLElement>('[data-warm-id]')].find(
        (x) => x.dataset.warmId === id && x.getBoundingClientRect().height > 0,
      );
      if (!now) return;
      const d = now.getBoundingClientRect().top - top;
      if (Math.abs(d) < 1) return;
      if (root) root.scrollTop += d;
      else window.scrollBy(0, d);
    };
  }
  return null;
}

/** Подменить показанные данные: с удержанием прокрутки, если на экране есть прокрученный список. */
function showData(entry: Entry, patch: Partial<Snapshot>): void {
  const fix = entry.listeners.size > 0 && entry.snap.data != null ? captureScrollAnchor() : null;
  setSnap(entry, patch);
  if (fix) requestAnimationFrame(fix);
}

/** Снимки одинаковы? Данные подменяются только ЦЕЛИКОМ (иммутабельно), поэтому ссылки хватает. */
function sameSnap(a: Snapshot, b: Snapshot): boolean {
  return (
    a.data === b.data &&
    a.loading === b.loading &&
    a.stale === b.stale &&
    a.error === b.error &&
    a.errorStatus === b.errorStatus &&
    a.confirmed === b.confirmed
  );
}

function setSnap(entry: Entry, patch: Partial<Snapshot>): void {
  const next: Snapshot = { ...entry.snap, ...patch };
  // ТИХИЙ ОПРОС НЕ ПЕРЕРИСОВЫВАЕТ ЭКРАН (01.09.2026, разбор зависаний «Чатов»). Ответ
  // пришёл прежним — менять нечего, но useSyncExternalStore сверяет снимки ПО ССЫЛКЕ:
  // новый объект с теми же значениями поднимал полную перерисовку списка переписок на
  // каждом опросе (каждые 30 секунд, а с живым каналом — на каждое входящее). Значения
  // те же → и снимок остаётся ТОТ ЖЕ, слушателей не дёргаем.
  if (sameSnap(entry.snap, next)) return;
  entry.snap = next;
  for (const listener of entry.listeners) listener();
}

const nowMs = (): number => (typeof performance !== 'undefined' ? performance.now() : Date.now());

/**
 * Сколько заняла перерисовка после подмены данных: от setState до ближайшего кадра.
 * Пишем только долгое и только на ВИДИМОЙ странице: в свёрнутой вкладке кадра нет вовсе,
 * и «перерисовка 40 минут» была бы враньём (тот же капкан, что у часов сторожа).
 * Замер ничего не меняет — он только кладёт строку в журнал.
 */
function noteRender(key: string): void {
  if (typeof requestAnimationFrame === 'undefined' || typeof document === 'undefined') return;
  if (document.visibilityState !== 'visible') return;
  const t = nowMs();
  requestAnimationFrame(() => {
    if (document.visibilityState !== 'visible') return;
    noteTask(`render ${key}`, nowMs() - t);
  });
}

/** Очистить весь персистентный кэш и in-memory стор (вызывать при логауте — чтобы чужие данные не остались). */
/** Забыть ОДИН ключ: и запись на устройстве, и снимок в памяти. Нужно там, где под
 *  временным ключом мог осесть чужой снимок (черновик 'new' до рождения настоящего id). */
export function forgetPersistent(key: string): void {
  try {
    window.localStorage.removeItem(PREFIX + key);
  } catch {
    /* нет доступа — не критично */
  }
  writeCacheAt(key, null);
  const entry = store.get(key);
  if (!entry) return;
  entry.epoch += 1;
  entry.inFlight = false;
  entry.settledAt = 0;
  entry.dataAt = 0;
  entry.stash = null;
  delete entry.dataVer;
  writeCacheVer(key, undefined);
  dropEtag(key, entry);
  setSnap(entry, { data: null, loading: false, stale: false, error: false, errorStatus: null, confirmed: false });
}

/**
 * ПЕРЕЧИТАТЬ ВСЕ КЛЮЧИ С ПРИСТАВКОЙ — после мутации, которая меняет данные не одного
 * ключа (владелец 24.09.2026: «удалил метку — должна исчезнуть везде»: свод меток,
 * списки людей с их метками, папки). Перечитываются только ключи, которые в этой
 * сессии уже запрашивались (у них есть параметры запроса); подписчики получают новые
 * данные тем же путём, что при обычной ревалидации.
 */
export function revalidatePersistentPrefix(prefix: string): void {
  for (const [key, entry] of store.entries()) {
    if (!key.startsWith(prefix) || !entry.params) continue;
    // Причина — правка/живое событие: подмена после неё законна (детектор её не считает).
    void revalidate(key, { ...entry.params, cause: 'prefix' });
  }
}

/**
 * ДАННЫЕ С ПРИСТАВКОЙ БОЛЬШЕ НЕ СЧИТАЮТСЯ ПОДТВЕРЖДЁННЫМИ (быстрый запуск, 28.10.2026): после мутации, которая меняет
 * список (созданы боты), копия на устройстве остаётся для первого кадра, но время и версия копии снимаются — следующее
 * открытие экрана (в том числе после перезагрузки страницы) обязательно сверится с сервером, а не сочтёт копию
 * свежей ещё 60 с. Без этого запрос `refreshPersistent`, оборванный переходом страницы, оставлял список без новых ботов.
 */
export function stalePersistentPrefix(prefix: string): void {
  if (typeof window !== 'undefined') {
    try {
      for (const k of Object.keys(window.localStorage)) {
        if (!k.startsWith(PREFIX + prefix)) continue;
        const key = k.slice(PREFIX.length);
        writeCacheAt(key, null);
        writeCacheVer(key, undefined);
      }
    } catch {
      /* нет доступа — не критично */
    }
  }
  for (const [key, entry] of store.entries()) {
    if (!key.startsWith(prefix)) continue;
    entry.dataAt = 0;
    delete entry.dataVer;
  }
}

export function clearPersistentCache(): void {
  try {
    const keys = Object.keys(window.localStorage).filter((k) => k.startsWith(PREFIX));
    for (const k of keys) window.localStorage.removeItem(k);
    window.localStorage.removeItem(AT_KEY);
    window.localStorage.removeItem(VER_KEY);
    window.localStorage.removeItem(ETAG_KEY);
  } catch {
    /* нет доступа — не критично */
  }
  for (const entry of store.values()) {
    entry.epoch += 1;
    entry.inFlight = false;
    entry.settledAt = 0;
    entry.dataAt = 0;
    entry.stash = null;
    delete entry.dataVer;
    delete entry.etag;
    setSnap(entry, { data: null, loading: true, stale: false, error: false, errorStatus: null, confirmed: false });
  }
}

interface FetchParams {
  url: string;
  accept?: (raw: unknown) => boolean;
  fetchOptions?: RequestInit;
  merge?: (prev: unknown, next: unknown) => unknown;
  /** Адрес быстрого ответа из базы (П162 п.2) — см. Options.fastUrl. */
  fastUrl?: string;
  fastMerge?: (prev: unknown, raw: unknown) => unknown;
  /** Запрос прогрева (prefetchPersistent): ответ ложится в кэш, но окно MOUNT_FRESH_MS НЕ открывает — страница,
   *  открытая после прогрева, берёт кэш тем же кадром И ещё раз перезагружается в фоне (PREFETCH-TZ §1 п.3–4). */
  warm?: boolean;
  /** Запрос ОТКРЫТОГО экрана в момент открытия (маунт хука): передний план для очереди прогрева даже при данных в
   *  кэше — экран, вставший на лёгком прогреве (лента `?limit=20`), догружает полную страницу раньше фона
   *  (PREFETCH-CAREFUL §2 п.2, приёмка 11.10: прогрев обгонял полную ленту открытой переписки). */
  foreground?: boolean;
  /** Причина перечитки (журнал): 'reload' — действие человека/правка, 'prefix' — правка затронула несколько ключей /
   *  живое событие. */
  cause?: string;
  /** Версия сайта данных (`opts.version`): пишется вместе с ответом; совпала — данные верны без проверки. */
  version?: string;
}

/**
 * БЫСТРЫЙ ОТВЕТ ПЕРВЫМ (П162 п.2). Уходит ВМЕСТЕ с обычным запросом, один раз за сессию
 * на ключ; ложится, только если правда ещё не пришла (тот же epoch и запрос в пути), и
 * только то, что одобрил fastMerge (null — пропустить). Ошибки молчат: быстрый ответ —
 * ускорение, а не правда, и ни скелетона, ни ошибки он не рисует.
 */
async function revalidateFast(key: string, entry: Entry, params: FetchParams, epoch: number): Promise<void> {
  if (!params.fastUrl) return;
  const t0 = nowMs();
  let applied = 0;
  try {
    const r = await fetch(params.fastUrl, { cache: 'no-store', ...params.fetchOptions });
    if (!r.ok) return;
    const raw: unknown = await r.json();
    // Правда уже легла (запрос не в пути) или случились mutate/reload — быстрый устарел.
    if (entry.epoch !== epoch || !entry.inFlight) return;
    const prev = entry.snap.data;
    const next = params.fastMerge ? params.fastMerge(prev, raw) : raw;
    if (next == null) return;
    if (params.accept && !params.accept(next)) return;
    applied = 1;
    const shared = prev != null ? shareStructure(prev, next) : next;
    if (shared === prev) return;
    writeCache(key, shared);
    dropEtag(key, entry);
    entry.dataAt = Date.now();
    entry.dataWarmUrl = params.warm ? params.url : undefined;
    showData(entry, { data: shared, loading: false, stale: true, error: false, errorStatus: null });
    noteRender(key);
  } catch {
    /* быстрый ответ не обязателен — правда придёт обычным запросом */
  } finally {
    // Журнал: сколько ждали быстрый ответ и лёг ли он (П162 п.2 — мерить, а не гадать).
    logJournal('data.fast', { key: key.slice(0, 60), ms: Math.round(nowMs() - t0), applied });
  }
}

/**
 * Параметры запроса ключа: `If-None-Match` — когда показана копия, собранная из ответа ЭТОГО же адреса с `ETag`, и запрос —
 * обычный GET без своего условия. Старая ручка без ETag и копия без отпечатка — запрос как прежде (200 с телом).
 */
function conditionalInit(entry: Entry, params: FetchParams): { init: RequestInit; sent: boolean } {
  const base: RequestInit = { cache: 'no-store', ...params.fetchOptions };
  const method = (base.method ?? 'GET').toUpperCase();
  if (!entry.etag || entry.etag.u !== params.url || entry.snap.data == null || method !== 'GET') {
    return { init: base, sent: false };
  }
  const headers = new Headers(base.headers);
  if (headers.has('if-none-match')) return { init: base, sent: false };
  headers.set('If-None-Match', entry.etag.e);
  return { init: { ...base, headers }, sent: true };
}

async function revalidate(key: string, params: FetchParams): Promise<void> {
  const entry = getEntry(key);
  // Последние параметры запроса — чтобы ключ можно было перечитать снаружи, без
  // экземпляра хука (revalidatePersistentPrefix).
  entry.params = params;
  if (entry.inFlight) {
    // Дедуп: N компонентов с одним key = 1 запрос. Исключение — в полёте прогрев ДРУГИМ адресом (лёгкая лента
    // `?limit=20`, PREFETCH-CAREFUL §2 п.5): экран не ждёт его и не остаётся на урезанной странице — запрос экрана
    // уходит СРАЗУ, а ответ прогрева выбрасывается (epoch).
    if (params.warm || !entry.inFlightWarm || entry.inFlightUrl === params.url) return;
    entry.epoch += 1;
    entry.inFlight = false;
  }
  entry.inFlight = true;
  entry.inFlightUrl = params.url;
  entry.inFlightWarm = !!params.warm;
  entry.fetchedAt = Date.now();
  const epoch = entry.epoch;
  // КАСАНИЕ ГЛАВНЕЕ ПРОГРЕВА (PREFETCH-CAREFUL §2 п.2): первая загрузка открытого экрана (данных в кэше нет) —
  // передний план, фоновая очередь прогрева (warm.ts) на это время новых запросов не запускает. Фоновое
  // обновление при данных в кэше и сам прогрев — не передний план.
  const fg = !params.warm && (params.foreground || entry.snap.data == null) ? beginForeground(key) : 0;
  // Первый запрос ключа в сессии: быстрый ответ из базы уходит рядом с обычным (П162 п.2).
  if (params.fastUrl && !entry.fastTried) {
    entry.fastTried = true;
    void revalidateFast(key, entry, params, epoch);
  }
  try {
    const cond = conditionalInit(entry, params);
    const r = await fetch(params.url, cond.init);
    if (entry.epoch !== epoch) return; // mutate/reload случились позже — этот ответ устарел
    // 304 (ТЗ SPEED §10.1): тело то же, что у показанной копии, — данные и ссылки не трогаем (перерисовки нет), освежаем
    // только время копии и «подтверждено сервером», как при ответе «то же самое».
    // Копию, на которую ответили 304, экран денег мог за время запроса убрать в `stash` (gateServerTruth) — она и встаёт.
    const kept = entry.snap.data ?? entry.stash;
    if (r.status === 304 && cond.sent && kept != null) {
      entry.settledAt = params.warm ? 0 : Date.now();
      entry.dataAt = Date.now();
      entry.stash = null;
      if (!params.warm) entry.dataWarmUrl = undefined;
      writeCacheAt(key, entry.dataAt);
      setSnap(entry, { data: kept, loading: false, stale: false, error: false, errorStatus: null, confirmed: true });
      return;
    }
    // Ручки админки (/api/admin/*) с ТЗ ADM-NATIVE §4.2 отвечают 401 на мёртвую сессию (раньше 403):
    // для них 401 — обычная ошибка данных, ровно как прежний 403, — браузерная админка не меняется.
    if (r.status === 401 && !isAdminApi(params.url)) {
      clearPersistentCache();
      window.location.assign('/login');
      return;
    }
    if (!r.ok) {
      failTo(entry, r.status);
      return;
    }
    // ЗАМЕР ОБРАБОТКИ ОТВЕТА (01.09.2026, инцидент f26f9ee4: экран «Чатов» стоит 3–7 секунд
    // в покое, хотя опрос отвечает за 200 мс). Разбор JSON, сверка с прежним и подмена
    // данных идут на главном потоке — если поток держат здесь, это должно быть видно
    // в журнале строкой, а не гаданием.
    const t0 = nowMs();
    const raw: unknown = await r.json();
    const jsonMs = nowMs() - t0;
    if (entry.epoch !== epoch) return;
    const accepted = params.accept ? params.accept(raw) : raw != null;
    if (!accepted) {
      failTo(entry, r.status);
      return;
    }
    const prev = entry.snap.data;
    // merge: компонент может «дособрать» ответ из прежних данных (напр. warming-ответ
    // при спящем сервере не должен затирать живой список из кэша).
    const merged = params.merge && prev != null ? params.merge(prev, raw) : raw;
    // STRUCTURAL SHARING: равное — те же ссылки; всё равно — `prev` целиком (ни одной перерисовки).
    const next = prev != null ? shareStructure(prev, merged) : merged;
    const same = prev != null && next === prev;
    // Отпечаток годен, только если показанное = этот ответ целиком (без дособирания `merge` из прежних данных).
    const tag = typeof r.headers?.get === 'function' ? r.headers.get('etag') : null;
    if (tag && !(params.merge && prev != null)) {
      entry.etag = { u: params.url, e: tag };
      writeCacheEtag(key, entry.etag);
    } else dropEtag(key, entry);
    entry.settledAt = params.warm ? 0 : Date.now();
    entry.dataAt = Date.now();
      entry.stash = null;
    if (params.version) {
      entry.dataVer = params.version;
      writeCacheVer(key, params.version);
    }
    // Прогрев чужим адресом (лёгкая лента) — помечаем, экран со своим адресом его догрузит; ответ экрана снимает метку.
    if (params.warm) {
      if (prev == null || !same) entry.dataWarmUrl = params.url;
    } else entry.dataWarmUrl = undefined;
    if (same) {
      writeCacheAt(key, entry.dataAt); // данные те же — освежаем только время копии
      setSnap(entry, { loading: false, stale: false, error: false, errorStatus: null, confirmed: true }); // не изменилось — без мигания
    } else {
      writeCache(key, next);
      showData(entry, { data: next, loading: false, stale: false, error: false, errorStatus: null, confirmed: true });
    }
    noteTask(`data ${key}`, nowMs() - t0, { json: Math.round(jsonMs), ...(same ? {} : { chg: 1 }) });
    if (!same) noteRender(key);
  } catch {
    if (entry.epoch === epoch) failTo(entry, null);
  } finally {
    if (fg) endForeground(fg);
    if (entry.epoch === epoch) entry.inFlight = false;
  }
}

/**
 * Запрос упал (сеть, не-2xx, невалидное тело): данные не трогаем. Показывать было нечего (заглушка), а на устройстве
 * лежит старая копия — она встаёт вместо пустоты с error=true (экран честно говорит «не обновилось»): канон «ошибка
 * сети → кэш остаётся» (docs/HOW-IT-WORKS.md §3 «Сайт»).
 */
function failTo(entry: Entry, status: number | null): void {
  if (entry.snap.data == null && entry.stash != null) {
    const data = entry.stash;
    entry.stash = null;
    setSnap(entry, { data, loading: false, stale: false, error: true, errorStatus: status });
    return;
  }
  setSnap(entry, { loading: false, stale: false, error: true, errorStatus: status });
}

function forceRevalidate(key: string, params: FetchParams): void {
  const entry = getEntry(key);
  entry.epoch += 1; // in-flight ответ (если был) будет выброшен
  entry.inFlight = false;
  setSnap(entry, { stale: true, error: false, errorStatus: null });
  // reload() — действие человека или правка: подмена после него законна.
  void revalidate(key, { ...params, cause: params.cause ?? 'reload' });
}

function mutateEntry(key: string, next: unknown): void {
  const entry = getEntry(key);
  entry.epoch += 1; // отменяем in-flight, чтобы устаревший ответ не перезатёр оптимистику
  entry.inFlight = false;
  writeCache(key, next);
  dropEtag(key, entry);
  entry.dataAt = Date.now();
  entry.dataWarmUrl = undefined;
  entry.stash = null;
  setSnap(entry, { data: next, loading: false, stale: false, error: false, errorStatus: null, confirmed: true });
}

interface Options<T> {
  /** Принять ответ в кэш? По умолчанию — любой непустой объект. Обычно: (j) => !!j?.ok */
  accept?: (raw: unknown) => boolean;
  /** Доп. опции fetch (по умолчанию GET, no-store). */
  fetchOptions?: RequestInit;
  /** Секретные данные (токены и т.п.): держать только in-memory, НЕ писать в localStorage. */
  sensitive?: boolean;
  /**
   * БЫСТРЫЙ ОТВЕТ ИЗ БАЗЫ (П162 п.2): адрес той же ручки с `fast=1`. Уходит один раз за
   * сессию вместе с первым обычным запросом ключа; ложится, только если обычный ещё не
   * пришёл, и только в виде, который вернул `fastMerge` (null — пропустить). Ключ и кэш —
   * те же, второй правды нет.
   */
  fastUrl?: string;
  /** Слить быстрый ответ с показанным (prev — null на холодном старте без кэша). */
  fastMerge?: (prev: T | null, raw: unknown) => T | null;
  /**
   * Дособрать принятый ответ из прежних данных (зовётся только когда prev уже есть).
   * Нужен для warming-ответов при спящем сервере: живые части (список сценариев,
   * аккаунты, таблицы) берутся из prev, чтобы кэш не затёрся пустотой на время прогрева.
   */
  merge?: (prev: T, next: T) => T;
  /**
   * Имя формы экрана (ред. 9, 7.4.1): пока свежих данных нет (`loading`), хук отдаёт её в `shape` — устройство →
   * `components/cabinet/shapes` → копия запасных форм сервера → null. Обычно экран рисует `<Shape name=…/>` сам.
   */
  shape?: string;
  /** Параметры вида формы (`days`, `only`… — не id и не поиск). */
  shapeView?: Record<string, string>;
  /**
   * ВЕРСИОНИРОВАННОЕ СОДЕРЖИМОЕ (ТЗ HELP-WARM-FIX §1 п.6; практика — кэш без срока, сброс по версии): оглавление
   * справки меняется только с выкаткой. Данные, полученные с этой версией сайта (`NEXT_PUBLIC_BUILD_ID`), верны, пока
   * версия та же — сервер не проверяется ни при открытии, ни по возврату в окно.
   */
  version?: string;
}

export interface PersistentData<T> {
  /** Данные из кэша или свежие. null — только пока ни разу не загрузили (холодный старт). */
  data: T | null;
  /** true, пока сохранённого нет вовсе и ответ ещё не пришёл → экран рисует заглушку формы (`<Shape/>`). У экранов денег и
   *  статусов (SERVER_TRUTH) — пока нет свежего (≤ MOUNT_FRESH_MS). */
  loading: boolean;
  /** Данные подтверждены сервером (ответ в этой сессии или моложе MOUNT_FRESH_MS). Элемент из SERVER_TRUTH с
   *  `mode: 'field'` (тумблер «бот включён») до этого стоит заглушкой. */
  confirmed: boolean;
  /** Форма экрана для заглушки (только при `loading` и `opts.shape`), иначе null. */
  shape: Shape | null;
  /** Данные показаны, фоновая перечитка ещё идёт. */
  stale: boolean;
  /** Последняя ревалидация упала (сеть/не-2xx/невалидное тело). Данные остаются прежними. */
  error: boolean;
  /** HTTP-статус упавшей ревалидации (отличить 404 «нет такого» от обрыва сети — null). */
  errorStatus: number | null;
  /** Принудительно перезапросить (напр. после мутации на сервере). */
  reload: () => void;
  /** Локально заменить данные + кэш (оптимистичные обновления). */
  mutate: (next: T) => void;
}

/**
 * Главные списки разделов кабинета — их, и только их, освежает показ панели стека (ТЗ WEB-CAUSES
 * М42, 06.10.2026): «Боты» — список ботов, «Чаты» — переписки (и их отборы `cabinet:messages:*`),
 * «Контакты» — люди, «Рассылки» — рассылки. У «Аккаунта» своего списка нет: лента уведомлений
 * и строки освежаются по интервалу и возврату в окно.
 */
const SECTION_MAIN_LISTS = ['cabinet:scenarios', 'cabinet:messages', 'cabinet:contacts', 'cabinet:broadcasts'];
export function isSectionMainList(key: string): boolean {
  return SECTION_MAIN_LISTS.some((k) => key === k || key.startsWith(`${k}:`));
}

export function usePersistentData<T>(
  key: string,
  url: string,
  opts: Options<T> = {},
): PersistentData<T> {
  if (opts.sensitive) registerSensitive(key); // до первого getEntry, чтобы кэш не успел прочитаться
  const paramsRef = useRef<FetchParams>({ url });
  paramsRef.current = {
    url,
    accept: opts.accept,
    fetchOptions: opts.fetchOptions,
    merge: opts.merge as FetchParams['merge'],
    ...(opts.fastUrl ? { fastUrl: opts.fastUrl, fastMerge: opts.fastMerge as FetchParams['fastMerge'] } : {}),
    ...(opts.version ? { version: opts.version } : {}),
  };
  const version = opts.version;

  // Экраны денег и статусов (SERVER_TRUTH): старое в памяти, которое сейчас никто не показывает, уходит в заглушку
  // ДО чтения снимка. Прочие ключи — сохранённое в первый кадр всегда (INVARIANT у MOUNT_FRESH_MS).
  if (typeof window !== 'undefined' && url) gateServerTruth(key);

  const subscribe = useCallback((onChange: () => void) => {
    const entry = getEntry(key);
    entry.listeners.add(onChange);
    return () => {
      entry.listeners.delete(onChange);
    };
  }, [key]);

  const snap = useSyncExternalStore(
    subscribe,
    () => getEntry(key).snap,
    () => SERVER_SNAPSHOT,
  );

  useEffect(() => {
    // Пустой url — намеренный пропуск запроса (напр. черновик ещё не создан, id='new'):
    // держим начальный снимок, не бьём в сеть.
    if (!url) return;
    // ДАННЫЕ ВЕРНЫ (моложе MOUNT_FRESH_MS: прогрев, другой экран, mutate; или той же версии сайта) — сети при открытии
    // НЕТ. Иначе сохранённое уже стоит в первом кадре, а сервер проверяется фоном; ответ меняет только изменившееся.
    const e = getEntry(key);
    if (isFreshFor(e, url, version)) return;
    // Передний план для очереди прогрева — только когда экрану нечего показать (заглушка) или он стоит на урезанном
    // прогреве чужим адресом (лёгкая лента): тогда его запрос главнее фона (PREFETCH-CAREFUL §2 п.2).
    const fg = e.snap.data == null || (e.dataWarmUrl !== undefined && e.dataWarmUrl !== url);
    void revalidate(key, { ...paramsRef.current, ...(fg ? { foreground: true } : {}) });
  }, [key, url, version]);

  // ВСЯКИЙ ПОКАЗ ОСВЕЖАЕТ ДАННЫЕ (владелец 2026-08-21, Р5). Канон кабинета: экран всегда
  // мгновенно рисует кэш, а в фоне уходит запрос и тихо подменяет показанное. Раньше это
  // работало только наполовину: сигналом был «маунт» да возврат в вкладку, а разделы
  // кабинета живут в вечном стеке БЕЗ размонтирования (tab-stack) — переключение раздела
  // и выбор пункта во второй колонке не считались показом вовсе, и человек смотрел на
  // вчерашний кэш, пока не нажимал обновление страницы.
  // Сигналов теперь три, и слушает их САМ ХУК (россыпи ручных «paneActive → reload» по
  // страницам больше не нужны):
  //   • возврат в вкладку/окно (focus, visibilitychange);
  //   • оживление страницы из bfcache Safari (pageshow с persisted — эффекты при нём
  //     молчат, и без этого страница висела старой до Cmd+R);
  //   • панель раздела снова стала АКТИВНОЙ в стеке (PaneActiveContext false→true).
  // Общий тормоз FOCUS_MIN_MS на ключ бережёт сервер: у одного visibility-события в
  // стеке слушателей столько же, сколько открытых разделов, а на iOS такие события
  // приходят пачками при свайпах.
  useEffect(() => {
    if (!url) return;
    const wake = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - getEntry(key).fetchedAt < FOCUS_MIN_MS) return;
      // Версионированное содержимое той же версии сайта — проверять нечего.
      const ve = getEntry(key);
      if (version && ve.dataVer === version && ve.snap.data != null) return;
      void revalidate(key, paramsRef.current);
    };
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) wake();
    };
    window.addEventListener('focus', wake);
    window.addEventListener('pageshow', onPageShow);
    document.addEventListener('visibilitychange', wake);
    return () => {
      window.removeEventListener('focus', wake);
      window.removeEventListener('pageshow', onPageShow);
      document.removeEventListener('visibilitychange', wake);
    };
  }, [key, url, version]);

  // Показ панели стека. Провайдера нет (открытый бот, админка) → значение по умолчанию
  // true и эффект просто не находит перехода false→true: поведение вне стека прежнее.
  // ПРИ ВОЗВРАТЕ НА ВКЛАДКУ ОСВЕЖАЕТСЯ ТОЛЬКО ГЛАВНЫЙ СПИСОК РАЗДЕЛА (ТЗ WEB-CAUSES М42, решение
  // 06.10.2026, уточняет Р5 21.08): возврат в «Боты» слал 12 запросов (каталог шаблонов, истории
  // справки, ниша, пульс, аккаунты, схемы…), «Чаты» — 5, и всё это било по главному потоку в момент
  // смены вкладки. Теперь показ панели будит только ключи isSectionMainList; остальное — по
  // интервалу, focus/visibilitychange/pageshow (эффект выше) и явным reload.
  const paneActive = useContext(PaneActiveContext);
  const wasPaneActive = useRef(paneActive);
  useEffect(() => {
    const shown = paneActive && !wasPaneActive.current;
    wasPaneActive.current = paneActive;
    if (!shown || !url || !isSectionMainList(key)) return;
    if (Date.now() - getEntry(key).fetchedAt < FOCUS_MIN_MS) return;
    void revalidate(key, paramsRef.current);
  }, [key, url, paneActive]);

  const reload = useCallback(() => forceRevalidate(key, paramsRef.current), [key]);
  const mutate = useCallback((next: T) => mutateEntry(key, next), [key]);

  const shapeName = opts.shape;
  const shapeView = opts.shapeView;
  const shape = snap.loading && shapeName && typeof window !== 'undefined' ? readShape(shapeName, shapeView) : null;

  return {
    data: snap.data as T | null,
    loading: snap.loading,
    confirmed: snap.confirmed,
    shape,
    stale: snap.stale,
    error: snap.error,
    errorStatus: snap.errorStatus,
    reload,
    mutate,
  };
}

/**
 * ЧТЕНИЕ КЛЮЧА БЕЗ СЕТИ (правило 7 владельца 11.09.2026: соседние папки «Чатов» рисуются
 * из кэша кабинета, опрос сервера ведёт только текущая). Снимок берётся из общего стора,
 * а его нет — с устройства; сетевого запроса не случается ни при каких условиях.
 * Второго кэша не заводим: это тот же стор и те же ключи, что у usePersistentData.
 */
export function peekPersistent<T>(key: string): T | null {
  if (typeof window === 'undefined' || !key) return null;
  return getEntry(key).snap.data as T | null;
}

/** Последний запрос ключа упал (ответ не принят или сеть) — для журнала прогрева (warm.ts `adm:warm`). */
export function persistentFailed(key: string): boolean {
  if (typeof window === 'undefined' || !key) return false;
  return getEntry(key).snap.error;
}

/** Следить за ключом, НЕ запрашивая его: прогрев доехал — слушателя дёрнут. */
export function subscribePersistent(key: string, onChange: () => void): () => void {
  if (typeof window === 'undefined' || !key) return () => undefined;
  const entry = getEntry(key);
  entry.listeners.add(onChange);
  return () => {
    entry.listeners.delete(onChange);
  };
}

/** Снимок пустого списка ключей — ОДИН объект на все вызовы: useSyncExternalStore сверяет
 *  снимки по ссылке, и новый массив на каждый рендер крутил бы перерисовку без конца. */
const EMPTY_PEEK: unknown[] = [];

/**
 * НЕСКОЛЬКО КЛЮЧЕЙ РАЗОМ, ТОЛЬКО ИЗ КЭША. Возвращает по значению на ключ в том же
 * порядке; ключа в кэше нет — null. Запросов не делает вовсе (греет их тот, кому надо —
 * prefetchPersistent), но на приезд прогрева перерисовывается.
 */
export function usePersistentPeek<T>(keys: string[]): (T | null)[] {
  const keysRef = useRef(keys);
  keysRef.current = keys;
  // Ключи — строкой: массив пересобирается каждым рендером, и подписку по нему
  // переоткрывало бы на каждый чих.
  const keysId = keys.join('\n');
  const subscribe = useCallback(
    (onChange: () => void) => {
      const offs = keysRef.current.map((k) => subscribePersistent(k, onChange));
      return () => {
        for (const off of offs) off();
      };
    },
    [keysId],
  );
  const lastRef = useRef<(T | null)[]>(EMPTY_PEEK as (T | null)[]);
  const getSnapshot = useCallback((): (T | null)[] => {
    const next = keysRef.current.map((k) => peekPersistent<T>(k));
    const prev = lastRef.current;
    // Значения те же — отдаём ТОТ ЖЕ массив (иначе бесконечная перерисовка).
    if (prev.length === next.length && prev.every((v, i) => v === next[i])) return prev;
    lastRef.current = next;
    return next;
  }, [keysId]);
  const getServerSnapshot = useCallback((): (T | null)[] => EMPTY_PEEK as (T | null)[], []);
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

/**
 * ПРОГРЕВ кэша без монтирования компонента — ТОЛЬКО для исполнителя плана прогрева (`lib/warm.ts`; замок
 * warm-plan.site.test.ts: вне исполнителей не зовётся). Страницы называют цель плана (`warmTarget`). Данные ложатся в
 * тот же стор и localStorage — открытая позже страница берёт их тем же кадром.
 */
export function prefetchPersistent<T>(key: string, url: string, opts: Options<T> = {}): Promise<void> {
  if (typeof window === 'undefined' || !url) return Promise.resolve();
  if (opts.sensitive) registerSensitive(key);
  // Промис — для очереди прогрева (lib/warm.ts: один запрос за раз).
  return revalidate(key, {
    url,
    warm: true,
    accept: opts.accept,
    fetchOptions: opts.fetchOptions,
    merge: opts.merge as FetchParams['merge'],
    ...(opts.fastUrl ? { fastUrl: opts.fastUrl, fastMerge: opts.fastMerge as FetchParams['fastMerge'] } : {}),
    ...(opts.version ? { version: opts.version } : {}),
  });
}

/**
 * ПЕРЕЧИТАТЬ КЛЮЧ СЕЙЧАС — после правки (новый бот → список «Ботов», сохранили форму → свои сервисы) или когда экран
 * сам ждёт ответ (котировка выбранного числа, следующая страница ленты). Это не прогрев: запрос уходит сразу, мимо
 * очереди, как `reload()` экрана; ответ ложится в тот же кэш. Промис — конец запроса.
 */
export function refreshPersistent<T>(key: string, url: string, opts: Options<T> = {}): Promise<void> {
  if (typeof window === 'undefined' || !url) return Promise.resolve();
  if (opts.sensitive) registerSensitive(key);
  return revalidate(key, {
    url,
    accept: opts.accept,
    fetchOptions: opts.fetchOptions,
    merge: opts.merge as FetchParams['merge'],
    cause: 'reload',
    ...(opts.version ? { version: opts.version } : {}),
  });
}

/** Данные ключа ещё верны для адреса `url` (моложе MOUNT_FRESH_MS или той же версии сайта) — прогрев их не трогает. */
export function persistentCurrent(key: string, url: string, version?: string): boolean {
  if (typeof window === 'undefined' || !key) return false;
  const e = getEntry(key);
  return isFreshFor(e, url, version ?? e.params?.version);
}
